// ============================================================
// my-organization-security.nav.ts — Phase-4 migration descriptor
// ------------------------------------------------------------
// Component-free, data-only navigation descriptor for the organization
// Security group (Members / Member roles / Api keys). Imports ONLY pure
// types/constants (@twist/ui-navigation types + @twist/core security
// primitives) — no component / feature code — so the registry stays a
// pure data graph.
//
// Mirrors the legacy sideNavConfig "Organization › Security" group: the
// group-parent required requireOrganization + MyOrganization__Members_Read,
// and each sub-item carried its own stricter read perm. We keep those
// STRICTER nav constraints (the my-organization routes are only
// authenticated-only) so nav-visible ⊆ route-accessible.
//
// Whole-group migration: the children carry the `sidebar` surface so the
// legacy "Security" group de-dups out (every sub-item path is piloted).
// The registry projects this descriptor as a SINGLE top-level "Security"
// leaf (→ /my-organization/members); the children power de-dup + the
// command palette + breadcrumb resolution.
// ============================================================
import { OrgPermissions } from '@twist/core';
import type { NavDescriptor } from '@twist/ui-navigation';

export const myOrganizationSecurityNav: NavDescriptor = {
  id: 'organization-security',
  path: '/my-organization/members',
  label: 'Security',
  icon: 'gearsDatabase',
  group: 'Organization',
  // Organization section = 20s; Security last (after Products, Devices,
  // Metadata) — matches the recovered sideNavConfig order.
  order: 23,
  surfaces: ['sidebar', 'palette'],
  security: {
    requireOrganization: true,
    orgPermissions: [OrgPermissions.MyOrganization__Members_Read],
  },
  children: [
    {
      id: 'organization-members',
      path: '/my-organization/members',
      label: 'Members',
      group: 'Organization',
      surfaces: ['sidebar', 'palette'],
      security: {
        requireOrganization: true,
        orgPermissions: [OrgPermissions.MyOrganization__Members_Read],
      },
    },
    {
      id: 'organization-member-roles',
      path: '/my-organization/roles',
      label: 'Member roles',
      group: 'Organization',
      surfaces: ['sidebar', 'palette'],
      security: {
        requireOrganization: true,
        orgPermissions: [OrgPermissions.MyOrganization__CustomRoles_Read],
      },
    },
    {
      id: 'organization-api-keys',
      path: '/my-organization/api-keys',
      label: 'Api keys',
      group: 'Organization',
      surfaces: ['sidebar', 'palette'],
      security: {
        requireOrganization: true,
        orgPermissions: [OrgPermissions.MyOrganization__ApiKeys_Read],
      },
    },
  ],
};
